import * as React from "react";
import styled from "@emotion/styled";
import { FlexRow, H3, Text } from "atoms";
import { hexToRGB, preventInvalidForwarding } from "styles/styleUtils";

/*
 * TODO: removed due to paginated reviews
type TagName = React.ComponentProps<typeof ReviewTag>["variant"];
type SortedTags = [string, number][];
 */

interface Props {
  type: "ARTIST" | "PROJECT";
  max?: number;
  ratings: {
    [key: string]: {
      value: number;
      icon: React.ReactNode;
    };
  };

  /*
   * TODO: removed due to paginated reviews
  tags: {
    [Property in TagName]?: SortedTags;
  };
   */
}

function ReviewStats({ type, ratings, max, ...props }: Props) {
  return (
    <Section {...props}>
      <SH3>{type === "ARTIST" ? "Reaction Overview" : "Project Ratings"}</SH3>
      <Text size="xxxs">{max} reviews</Text>
      <ReactionList entityType={type}>
        {Object.entries(ratings).map(([key, data]) => {
          const orderValue = data.value * -1;
          if (type === "ARTIST" && data.value > 0) {
            return (
              <ReactionItem order={orderValue} key={`artist-${key}`}>
                <ReactionIcon>
                  {data.icon}
                  <ReactionIconCount>{data.value}</ReactionIconCount>
                </ReactionIcon>
                <Text size="xxs" font="display" textTransform="uppercase">
                  {key}
                </Text>
              </ReactionItem>
            );
          }

          if (type === "PROJECT") {
            const mathMax = max || 0;
            return (
              <ReactionItem key={`project-${key}`}>
                <ReactionIcon>
                  {data.icon}
                  <ReactionIconCount>{data.value}</ReactionIconCount>
                </ReactionIcon>
                <div>
                  <RatingBar value={data.value} max={max as number} />
                  <RatingBarText justifyContent="space-between">
                    <Text size="xxxs">{key}</Text>
                    {data.value > 0 ? (
                      <Text size="xxxs">
                        {Math.round((data.value / mathMax) * 100)}%
                      </Text>
                    ) : (
                      <Text size="xxxs">0%</Text>
                    )}
                  </RatingBarText>
                </div>
              </ReactionItem>
            );
          }
          return null;
        })}
      </ReactionList>

      {/*
        * TODO: removed due to paginated reviews
      <TopTags>
        {Object.entries(tags).map(([tagName, tagStats]) =>
          !tagStats[0] ? null : (
            <ReviewTag
              variant={tagName as TagName}
              count={tagStats[0][1]}
              key={tagName as TagName}
            >
              {tagStats[0][0]}
            </ReviewTag>
          )
        )}
      </TopTags>
      */}
    </Section>
  );
}

const Section = styled.section({
  "& > div": {
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
    margin: "2rem 0 4rem",
    "@media (min-width: 500px)": {
      flexDirection: "row",
      gap: "1rem",
    },
    "@media (min-width: 800px)": {
      flexDirection: "column",
      gap: "2rem",
      marginBottom: 0,
    },
  },
});

const ReactionList = styled.ul<{ entityType: string }>(({ entityType }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 20,
  listStyleType: "none",
  margin: 0,
  maxWidth: 300,
  padding: "0 0 10px 0",

  ...(entityType === "ARTIST" && {
    li: {
      gap: 5,
    },
    "@media (max-width: 830px)": {
      flexDirection: "row",
      maxWidth: "100%",
      justifyContent: "stretch",
      width: "100%",
      flexWrap: "wrap",
      li: {
        flex: "1 0 140px",
        lineHeight: 1.1,
        gap: 5,
      },
    },
  }),

  ...(entityType === "PROJECT" && {
    li: {
      alignItems: "flex-end",
      lineHeight: "1.1",
      "@media (min-width: 720px) and (max-width: 830px)": {
        flex: "0 0 calc(25% - 15px)",
        lineHeight: "1.1",
      },
    },
  }),

  "@media (min-width: 720px) and (max-width: 830px)": {
    flexDirection: "row",
    maxWidth: "100%",
    justifyContent: "stretch",
    width: "100%",
    paddingTop: 0,
  },
}));

type OrderProps = {
  order?: number;
};

const ReactionItem = styled("li", {
  shouldForwardProp: preventInvalidForwarding<OrderProps>("order"),
})<OrderProps>(({ order, theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: 10,
  color: theme.colors.fg,
  position: "relative",
  order,
  "&>*": {
    flex: 1,
  },
  p: {
    margin: 0,
  },
  strong: {
    display: "block",
    fontWeight: "bold",
  },
  "&::before": {
    content: '"\\200B"',
    position: "absolute",
  },
}));

const ReactionIcon = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  borderRadius: theme.borderRadius.round,
  boxShadow: theme.boxShadow.light,
  flex: "0 0 47px !important",
  height: 47,
  width: 47,
  position: "relative",
  zIndex: 2,
  svg: {
    position: "relative",
    zIndex: 1,
    width: 47,
    height: "auto",
  },
}));
const ReactionIconCount = styled.span(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.colors.bg,
  border: `1px solid ${theme.colors.fg60}`,
  borderRadius: theme.borderRadius.round,
  bottom: 0,
  display: "flex",
  fontSize: 13,
  height: "1.3em",
  justifyContent: "center",
  lineHeight: 1,
  minWidth: "1.3em",
  padding: "0 3px",
  position: "absolute",
  right: 0,
  zIndex: 2,
}));

const SH3 = styled(H3)(({ theme }) => ({
  fontSize: theme.fontSizes.xs,
  fontWeight: "bold",
  marginBottom: 3,
}));

const RatingBar = styled.div<{ value: number; max: number }>(
  ({ value, max, theme }) => ({
    borderRadius: theme.borderRadius.round,
    backgroundColor: theme.colors.accent1LL,
    boxShadow: `0 0 0 1px ${hexToRGB(theme.colors.accent1, 0.66)}`,
    flex: 1,
    height: 16,
    lineHeight: 1.3,
    marginBottom: 2,
    marginLeft: -4,
    overflow: "hidden",
    position: "relative",
    zIndex: 0,
    "&::before": {
      alignItems: "center",
      backgroundColor: hexToRGB(theme.colors.accent1, 0.66),
      borderRadius: theme.borderRadius.round,
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
      content: "''",
      display: "flex",
      height: 16,
      position: "absolute",
      left: 0,
      width: value ? `${(value / max) * 100}%` : 0,
    },
    "li:nth-of-type(2) &": {
      backgroundColor: theme.colors.accent2LL,
      boxShadow: `0 0 0 1px ${hexToRGB(theme.colors.accent2, 0.5)}`,
      "&::before": {
        backgroundColor: hexToRGB(theme.colors.accent2, 0.5),
      },
    },
    "li:nth-of-type(3) &": {
      backgroundColor: theme.colors.accent3LL,
      boxShadow: `0 0 0 1px ${hexToRGB(theme.colors.accent3, 0.66)}`,
      "&::before": {
        backgroundColor: hexToRGB(theme.colors.accent3, 0.66),
      },
    },
    "li:nth-of-type(4) &": {
      backgroundColor: theme.colors.accent4LL,
      boxShadow: `0 0 0 1px ${hexToRGB(theme.colors.accent4, 0.6)}`,
      "&::before": {
        backgroundColor: hexToRGB(theme.colors.accent4, 0.6),
      },
    },
  }),
);

const RatingBarText = styled(FlexRow)({
  paddingLeft: 5,
  lineHeight: 1.3,
  flexWrap: "nowrap",
  gap: 6,
});
export { ReviewStats };
